import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Button,
  Tabs,
  Tab,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import confetti from 'canvas-confetti';
import { jsPDF } from 'jspdf';

import texasParks from '../../data/texasParks.json';
import nationalParks from '../../data/nationalParks.json';

function TabPanel({ children, value, index }) {
  return <div hidden={value !== index}>{value === index && <Box sx={{ py: 2 }}>{children}</Box>}</div>;
}

const sortParks = (parks) =>
  parks.map((p, i) => ({ ...p, originalIndex: i }))
       .sort((a, b) => a.name.localeCompare(b.name));

const Checklist = () => {
  const [tabValue, setTabValue] = useState(0);
  const [checkedTX, setCheckedTX] = useState({});
  const [checkedNational, setCheckedNational] = useState({});
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const savedTX = localStorage.getItem('checklist_tx_parks');
    const savedNational = localStorage.getItem('checklist_national_parks');
    if (savedTX) setCheckedTX(JSON.parse(savedTX));
    if (savedNational) setCheckedNational(JSON.parse(savedNational));
  }, []);

  useEffect(() => {
    localStorage.setItem('checklist_tx_parks', JSON.stringify(checkedTX));
  }, [checkedTX]);

  useEffect(() => {
    localStorage.setItem('checklist_national_parks', JSON.stringify(checkedNational));
  }, [checkedNational]);

  const triggerConfetti = () => {
    confetti({
      particleCount: 40,
      spread: 60,
      origin: { y: 0.8 },
    });
  };

  const handleTXCheck = (index) => {
    const isChecking = !checkedTX[index];
    setCheckedTX((prev) => ({ ...prev, [index]: isChecking }));
    if (isChecking) triggerConfetti();
  };

  const handleNationalCheck = (index) => {
    const isChecking = !checkedNational[index];
    setCheckedNational((prev) => ({ ...prev, [index]: isChecking }));
    if (isChecking) triggerConfetti();
  };

  const txParksProcessed = useMemo(() => {
    return sortParks(texasParks).filter((park) => {
      const isChecked = checkedTX[park.originalIndex];
      const matchesSearch = park.name.toLowerCase().includes(search.toLowerCase());
      if (!matchesSearch) return false;
      if (filter === 'visited') return isChecked;
      if (filter === 'not') return !isChecked;
      return true;
    });
  }, [checkedTX, search, filter]);

  const nationalParksProcessed = useMemo(() => {
    return sortParks(nationalParks).filter((park) => {
      const isChecked = checkedNational[park.originalIndex];
      const matchesSearch = park.name.toLowerCase().includes(search.toLowerCase());
      if (!matchesSearch) return false;
      if (filter === 'visited') return isChecked;
      if (filter === 'not') return !isChecked;
      return true;
    });
  }, [checkedNational, search, filter]);

  const txCheckedCount = Object.values(checkedTX).filter(Boolean).length;
  const nationalCheckedCount = Object.values(checkedNational).filter(Boolean).length;

  const txProgress = (txCheckedCount / texasParks.length) * 100;
  const nationalProgress = (nationalCheckedCount / nationalParks.length) * 100;

  const getBadge = (count) => {
    if (count === 0) return null;
    if (count < 5) return '🌱 Getting Started';
    if (count < 15) return '🌲 Explorer';
    if (count < 30) return '🏕️ Adventurer';
    return '🏆 Park Master';
  };

  const generatePDF = (parks, title) => {
  const doc = new jsPDF();

  const isTexas = title.toLowerCase().includes('texas');

  const theme = isTexas
    ? {
        primary: [74, 124, 89],   // Texas green
        header: [31, 61, 31],
      }
    : {
        primary: [30, 90, 140],   // National blue
        header: [15, 45, 70],
      };

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;

  // ===== HEADER =====
  doc.setFillColor(...theme.primary);
  doc.rect(0, 0, pageWidth, 22, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text(title, pageWidth / 2, 14, { align: 'center' });

  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text('Park Passport Checklist', pageWidth / 2, 20, { align: 'center' });

  doc.setTextColor(30, 30, 30);

  const columns = 3;
  const columnGap = 6;

  const colWidth =
    (pageWidth - margin * 2 - columnGap * (columns - 1)) / columns;

  const startY = 30;
  const lineHeight = 6;

  doc.setFontSize(8);

  // ===== NORMALIZED DATA (FIX FOR BUG) =====
  let data = [];

  if (isTexas) {
    // Texas = flat list of objects (NO STRINGS)
    data = parks
      .map((p) => ({ type: 'park', name: p?.name }))
      .filter((p) => p.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  } else {
    // National = grouped by state
    const grouped = parks.reduce((acc, park) => {
      const state = park?.state || 'Other';
      const name = park?.name;
      if (!name) return acc;

      if (!acc[state]) acc[state] = [];
      acc[state].push(name);

      return acc;
    }, {});

    const states = Object.keys(grouped).sort();

    states.forEach((state) => {
      data.push({ type: 'state', name: state });

      grouped[state]
        .sort((a, b) => a.localeCompare(b))
        .forEach((name) => {
          data.push({ type: 'park', name });
        });
    });
  }

  const chunkSize = Math.ceil(data.length / columns);

  // ===== RENDER =====
  for (let col = 0; col < columns; col++) {
    const items = data.slice(col * chunkSize, (col + 1) * chunkSize);

    let y = startY;
    const x = margin + col * (colWidth + columnGap);

    items.forEach((item) => {
      // State headers (National only)
      if (item.type === 'state') {
        doc.setFont(undefined, 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...theme.header);

        doc.text(item.name, x, y);

        doc.setFont(undefined, 'normal');
        doc.setFontSize(8);
        doc.setTextColor(30, 30, 30);

        y += 6;
        return;
      }

      // Checkbox
      doc.setDrawColor(120, 120, 120);
      doc.rect(x, y - 3, 3, 3);

      // Safe text render (extra safety)
      const text = typeof item.name === 'string' ? item.name : '';

      if (text) {
        let trimmed = text;
        const maxWidth = colWidth - 6;

        while (doc.getTextWidth(trimmed) > maxWidth) {
          trimmed = trimmed.slice(0, -1);
        }

        doc.text(trimmed, x + 5, y);
      }

      y += lineHeight;
    });
  }

  // ===== FOOTER =====
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);

  doc.text(
    `Generated from Your Park Passport • ${new Date().toLocaleDateString()}`,
    pageWidth / 2,
    pageHeight - 8,
    { align: 'center' }
  );

  doc.save(`${title.replace(/\s/g, '_')}_Passport.pdf`);
};

  const ChecklistCards = ({ parks, checked, onCheck }) => (
    <Grid container spacing={2}>
      {parks.map((park) => {
        const isChecked = checked[park.originalIndex] || false;

        return (
          <Grid item xs={12} sm={6} md={4} key={park.originalIndex}>
            <Card
              onClick={() => onCheck(park.originalIndex)}
              sx={{
                cursor: 'pointer',
                borderRadius: 3,
                transition: 'all 0.2s ease',
                border: isChecked ? '2px solid #4a7c59' : '1px solid #dbe7db',
                backgroundColor: isChecked ? '#f1f7f1' : '#fff',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Checkbox
                  checked={isChecked}
                  sx={{
                    color: '#4a7c59',
                    '&.Mui-checked': { color: '#1f3d1f' },
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      textDecoration: isChecked ? 'line-through' : 'none',
                    }}
                  >
                    {park.name}
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#6b8f71' }}>
                    {park.county || park.state}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: 4, maxWidth: 1200, mx: 'auto', backgroundColor: '#f9fbf8' }}>
      
      {/* HEADER */}
      <Typography variant="h4" align="center" sx={{ color: '#1f3d1f', fontWeight: 700 }}>
        Your Park Passport
      </Typography>

      <Typography align="center" sx={{ color: '#1f3d1f', mb: 3 }}>
        Track your adventures and explore more parks.
      </Typography>

      {/* SEARCH + FILTER */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <TextField
          fullWidth
          label="Search parks..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(e, val) => val && setFilter(val)}
          size="small"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="visited">Visited</ToggleButton>
          <ToggleButton value="not">Not Visited</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* PROGRESS + BADGE */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ mb: 1 }}>
          Progress: {tabValue === 0 ? txCheckedCount : nationalCheckedCount}
        </Typography>

        <LinearProgress
          variant="determinate"
          value={tabValue === 0 ? txProgress : nationalProgress}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: '#e6efe6',
            '& .MuiLinearProgress-bar': {
              backgroundColor: '#4a7c59',
            },
          }}
        />

        <Box sx={{ mt: 1 }}>
          <Chip label={getBadge(tabValue === 0 ? txCheckedCount : nationalCheckedCount)} />
        </Box>
      </Box>

      {/* DOWNLOAD */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => generatePDF(texasParks, 'Texas Parks')}
          sx={{ backgroundColor: '#4a7c59' }}
        >
          Download TX Passport
        </Button>

        <Button
          fullWidth
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={() => generatePDF(nationalParks, 'National Parks')}
          sx={{ backgroundColor: '#4a7c59' }}
        >
          Download National Passport
        </Button>
      </Box>

      {/* TABS */}
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="fullWidth">
        <Tab label={`Texas (${txCheckedCount})`} />
        <Tab label={`National (${nationalCheckedCount})`} />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <ChecklistCards parks={txParksProcessed} checked={checkedTX} onCheck={handleTXCheck} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <ChecklistCards parks={nationalParksProcessed} checked={checkedNational} onCheck={handleNationalCheck} />
      </TabPanel>
    </Box>
  );
};

export default Checklist;